import { FormField, FormTemplate, formTemplateService } from '@/services/formTemplateService';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import {
  AlertCircle,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
  Search,
  Ticket,
  User,
  UserCircle,
  X
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import omnichannelService, { Conversation, Message } from '../../services/omnichannelService';
import AgentAssignmentDropdown from './AgentAssignmentDropdown';
import AttachmentPreview from './AttachmentPreview';
import { FormTemplateSelector } from './FormTemplateSelector';
import MessageComposer from './MessageComposer';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger
} from '@/components/ui/custom/Dialog';
import TicketEscalationDialog from './TicketEscalationDialog';

interface ConversationDetailProps {
  conversationId: string;
  onUpdate: () => void;
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

// ---------------------------------------------------------------------------
// FormMessageBubble — renders a read-only preview of the form template that
// was sent to the customer. Fetches the template lazily on mount.
// ---------------------------------------------------------------------------

type FormMessageBubbleProps = {
  message: Message;
  theme: string;
  isOutbound: boolean;
};

function normaliseFields(schema: any): FormField[] {
  if (!schema) return [];
  if (Array.isArray(schema)) return schema as FormField[];
  if (Array.isArray(schema.fields)) return schema.fields as FormField[];
  const firstArray = Object.values(schema as Record<string, any>).find(Array.isArray);
  return (firstArray as FormField[]) ?? [];
}

function FormMessageBubble({ message, theme, isOutbound }: FormMessageBubbleProps) {
  const [template, setTemplate] = useState<{
    id: string;
    name: string;
    fields: FormField[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const templateId = message.channel_metadata?.form_template_id;
    if (!templateId) { setLoading(false); setError(true); return; }
    formTemplateService
      .getFormTemplate(Number(templateId))
      .then((t) => setTemplate({ id: templateId, name: t.name, fields: normaliseFields(t.schema) }))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [message.channel_metadata?.form_template_id]);

  const cardBase = isOutbound
    ? theme === 'dark'
      ? 'bg-blue-700 border-blue-500'
      : 'bg-blue-50 border-blue-300'
    : theme === 'dark'
      ? 'bg-gray-700 border-gray-600'
      : 'bg-white border-gray-200';

  const labelColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const valueColor = theme === 'dark' ? 'text-gray-100 bg-gray-800' : 'text-gray-800 bg-gray-100';
  const titleColor = isOutbound
    ? theme === 'dark' ? 'text-blue-200' : 'text-blue-700'
    : theme === 'dark' ? 'text-gray-100' : 'text-gray-800';

  if (loading) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cardBase}`}>
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        <span className={`text-xs ${labelColor}`}>Loading form…</span>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cardBase}`}>
        <FileText className="h-4 w-4 text-gray-400" />
        <span className={`text-xs ${labelColor}`}>
          {message.content || 'Form template'}
        </span>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${cardBase} overflow-hidden min-w-[240px] max-w-sm`}>
      {/* Header */}
      <div
        className={`flex items-center gap-2 px-3 py-2 border-b ${
          theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
        }`}
      >
        <FileText className={`h-4 w-4 flex-shrink-0 ${titleColor}`} />
        <Link to={`/settings/templates`}>
          <span className={`text-sm font-semibold ${titleColor}`}>{template.name}</span>
        </Link>
      </div>

      {/* Footer badge */}
      <div
        className={`px-3 py-1.5 border-t text-[10px] font-medium ${
          theme === 'dark' ? 'border-gray-600 text-gray-500' : 'border-gray-200 text-gray-400'
        }`}
      >
        Form sent to customer
        {/* Show submitted form data button */}
        {message.form_submission && (
          <Dialog>
            <DialogTrigger asChild>
              <button
                className={`ml-2 px-2 py-1 rounded text-xs transition-colors ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
                title="View submitted form data"
              >
                View Form Data
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Submitted Form Data</DialogTitle>
                <DialogDescription>
                  View the data submitted by the customer for this form.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {Object.entries(message.form_submission).map(([key, value]) => (
                  <div key={key} className="border-b pb-2">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      {key}:
                    </div>
                    <div className="text-sm text-gray-800 dark:text-gray-100">
                      {Array.isArray(value) ? JSON.stringify(value, null, 2) : String(value)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Submitted on:{' '}
                {message.submitted_at ? new Date(message.submitted_at).toLocaleString() : 'Unknown'}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------

export default function ConversationDetail({
  conversationId,
  onUpdate,
  isSidebarCollapsed = false,
  onToggleSidebar
}: ConversationDetailProps) {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [typingUser, setTypingUser] = useState<{ name: string; userId: number } | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { subscribeToOmnichannel } = useNotifications();

  // Search functionality
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState<number[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Form template attachment
  const [showFormTemplateSelector, setShowFormTemplateSelector] = useState<boolean>(false);

  // Ticket escalation dialog
  const [showTicketDialog, setShowTicketDialog] = useState<boolean>(false);

  // WebSocket for real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToOmnichannel((event) => {
      console.log('incoming socket event', event);

      if (event.type === 'new_message' && event.data.conversation_id === conversationId) {
        // Optimistically add new message to existing messages
        if (event.data.message) {
          setMessages((prev) => {
            // Check if message already exists to avoid duplicates
            const messageExists = prev.some((m) => m.id === event.data.message.id);
            if (messageExists) return prev;

            return [...prev, event.data.message];
          });
        }
        onUpdate?.();
      } else if (
        event.type === 'conversation_updated' &&
        event.data.conversation_id === conversationId
      ) {
        // Only reload conversation data, not messages
        loadConversation();
        onUpdate?.();
      } else if (
        event.type === 'typing_indicator' &&
        event.data.conversation_id === conversationId
      ) {
        // Don't show typing indicator for the current user
        if (event.data.user_id === user?.id) {
          return;
        }

        // Show typing indicator for other users
        if (event.data.is_typing && event.data.user_name) {
          setTypingUser({ name: event.data.user_name, userId: event.data.user_id || 0 });
        }

        // Clear existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // Hide typing indicator after 3 seconds
        typingTimeoutRef.current = setTimeout(() => {
          setTypingUser(null);
        }, 3000);
      } else if (
        event.type === 'message_status_updated' &&
        event.data.conversation_id === conversationId
      ) {
        // Update specific message status without full reload
        if (event.data.message_id && event.data.status) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === event.data.message_id ? { ...msg, status: event.data.status! } : msg
            )
          );
        }
      } else if (event.type === 'form_submitted' && event.data.conversation_id === conversationId) {
        // Update message with form submission data
        if (event.data.message_id && event.data.form_submission) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === event.data.message_id
                ? {
                    ...msg,
                    form_submission: event.data.form_submission,
                    submitted_at: event.data.submitted_at
                  }
                : msg
            )
          );
        }
        onUpdate?.();
      }
    });

    return () => {
      unsubscribe();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [conversationId, subscribeToOmnichannel, user]);

  useEffect(() => {
    // Reset state when conversation changes
    setMessages([]);
    setLoading(true);
    setHasMore(true);
    setIsInitialLoad(true);

    loadConversation();
    loadMessages();
    markAsRead();
  }, [conversationId]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (isInitialLoad && messages.length > 0) {
      scrollToBottom();
      setIsInitialLoad(false);
    }
  }, [messages, isInitialLoad]);

  const markAsRead = async () => {
    try {
      await omnichannelService.markConversationAsRead(conversationId);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Only auto-scroll to bottom on new messages (not when loading older messages)
  useEffect(() => {
    if (!loadingMore && !isInitialLoad && !isLoadingOlder) {
      scrollToBottom();
    }
  }, [messages.length]);

  const loadConversation = async () => {
    try {
      const response = await omnichannelService.getConversation(conversationId);
      setConversation(response);
    } catch (error) {}
  };

  const loadMessages = async () => {
    try {
      const data = await omnichannelService.getMessages(conversationId);

      setMessages(data);
      setIsInitialLoad(false);

      // Scroll to bottom on initial load
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { [key: string]: Message[] } = {};

    messages.forEach((message) => {
      const date = format(new Date(message.created_at), 'yyyy-MM-dd');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });

    return groups;
  };

  // Format date separator (e.g., "Mon 22:12", "Tue 21:00")
  const formatDateSeparator = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Check if it's today
    if (format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
      return 'Today';
    }

    // Check if it's yesterday
    if (format(date, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd')) {
      return 'Yesterday';
    }

    // Check if it's within 7 days, show day name (e.g., "Mon", "Tue")
    if (format(date, 'yyyy-MM-dd') >= format(today, 'yyyy-MM-dd')) {
      return format(date, 'EEE HH:mm');
    }

    // Otherwise show day name (e.g., "19 Oct 2025", "20 Oct 2025")
    return format(date, 'EEE dd MMM yyyy');
  };

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;

    try {
      setLoadingMore(true);
      setIsLoadingOlder(true);

      const oldestMessage = messages[0];

      // Save current scroll position and height
      const container = messagesContainerRef.current;
      if (!container) return;

      const scrollHeightBefore = container.scrollHeight;
      const scrollTopBefore = container.scrollTop;

      const olderMessages = await omnichannelService.getMessages(
        conversationId,
        50,
        oldestMessage.id
      );

      if (olderMessages && olderMessages.length > 0) {
        // Prepend older messages to the beginning
        setMessages((prev) => [...olderMessages, ...prev]);
        setHasMore(olderMessages.length >= 50);

        // Restore scroll position after DOM updates
        setTimeout(() => {
          const scrollHeightAfter = container.scrollHeight;
          const heightDifference = scrollHeightAfter - scrollHeightBefore;
          container.scrollTop = scrollTopBefore + heightDifference;
        }, 0);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('❌ Failed to load older messages:', error);
    } finally {
      setLoadingMore(false);
      // Reset after a short delay to allow scroll position to settle
      setTimeout(() => setIsLoadingOlder(false), 100);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Search functionality
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!searchQuery) {
      setSearchMatches([]);
      setCurrentMatchIndex(0);
      return;
    }

    // Find all messages that match the search query
    const matches: number[] = [];
    messages.forEach((message, index) => {
      if (message.content && message.content.toLowerCase().includes(searchQuery.toLowerCase())) {
        matches.push(index);
      }
    });
    setSearchMatches(matches);
    setCurrentMatchIndex(0);

    // Scroll to first match
    if (matches.length > 0) {
      scrollToMatch(0, matches);
    }
  }, [searchQuery, messages]);

  const scrollToMatch = (matchIndex: number, matches: number[]) => {
    if (matches.length === 0) return;

    const messageIndex = matches[matchIndex];
    const messageElements = messagesContainerRef.current?.querySelectorAll('[data-message-index]');
    if (messageElements && messageElements[messageIndex]) {
      messageElements[messageIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleNextMatch = () => {
    if (searchMatches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % searchMatches.length;
    setCurrentMatchIndex(nextIndex);
    scrollToMatch(nextIndex, searchMatches);
  };

  const handlePrevMatch = () => {
    if (searchMatches.length === 0) return;
    const prevIndex = currentMatchIndex === 0 ? searchMatches.length - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);
    scrollToMatch(prevIndex, searchMatches);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchMatches([]);
    setCurrentMatchIndex(0);
  };

  const highlightText = (text: string, query: string) => {
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark
          key={index}
          className="bg-yellow-300 dark:bg-yellow-600 text-gray-900 dark:text-white"
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handleSendMessage = async (content: string, attachments: File[]) => {
    try {
      await omnichannelService.sendMessage(conversationId, content, isInternalNote, attachments);
      setIsInternalNote(false);
      await loadMessages();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  };

  const handleStatusChange = async (status: string) => {
    try {
      // Check permission: admin or assigned to user
      const isAdmin = user?.role === 'admin';
      const isAssignedToMe = conversation?.assigned_to === user?.id;

      if (!isAdmin && !isAssignedToMe) {
        console.error(
          'Permission denied: You can only update status for conversations assigned to you'
        );
        return;
      }

      await omnichannelService.updateConversationStatus(conversationId, status);
      await loadConversation();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handlePriorityChange = async (priority: string) => {
    try {
      // Only admins can update priority
      if (user?.role !== 'admin') {
        console.error('Permission denied: Only admins can update priority');
        return;
      }

      await omnichannelService.updateConversationPriority(conversationId, priority);
      await loadConversation();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update priority:', error);
    }
  };

  const handleCreateTicket = () => {
    if (!conversation) return;
    setShowTicketDialog(true);
  };

  const handleCreateTicketSubmit = async (ticketData: {
    title: string;
    description: string;
    priority: string;
    status: string;
    group_id?: number;
    assigned_to_id?: number;
    form_template_id?: number;
    form_response?: any;
  }) => {
    if (!conversation) return;
    try {
      await omnichannelService.createTicketFromConversation(conversation.id, ticketData);
      await loadConversation();
      onUpdate?.();
    } catch (error: any) {
      console.error('Failed to create ticket:', error);
      // Show user-friendly error message
      const errorMessage =
        error?.response?.data?.error || 'Failed to create ticket. Please try again.';
      alert(errorMessage); // TODO: Replace with proper toast notification
      throw error; // Re-throw to let dialog handle the error
    }
  };

  const handleSendTemplate = async (template: FormTemplate) => {
    if (!conversation) return;
    try {
      await omnichannelService.sendFormMessage(conversationId, template.id);
      setShowFormTemplateSelector(false);
      await loadMessages();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to send form template:', error);
      throw error;
    }
  };

  const getSenderIcon = (senderType: string) => {
    switch (senderType) {
      case 'customer':
        // return <User className="h-5 w-5" />;
        return null;
      case 'agent':
        return <UserCircle className="h-5 w-5" />;
      case 'bot':
        return <Bot className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getMessageStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle2 className="h-3 w-3 text-gray-400" />;
      case 'delivered':
        return <CheckCircle2 className="h-3 w-3 text-blue-500" />;
      case 'read':
        return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      default:
        return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
          Conversation not found
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div
        className={`flex-shrink-0 border-b p-4 ${
          theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {conversation.customer_avatar ? (
              <img
                src={conversation.customer_avatar}
                alt={conversation.customer_name}
                className="h-10 w-10 rounded-full"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
            )}
            <div>
              <h2 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {conversation.customer_name}
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {conversation.customer_email}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Create Ticket Button */}
            {!conversation.ticket_id && (
              <button
                onClick={handleCreateTicket}
                className={`flex items-center gap-2 px-3 py-1 rounded text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
                title="Create Ticket"
              >
                <Ticket className="h-4 w-4" />
                <span>Create Ticket</span>
              </button>
            )}

            {/* View Ticket Button */}
            {conversation.ticket_id && (
              <a
                href={`/tickets/${conversation.ticket_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-3 py-1 rounded text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
                title="View Ticket"
              >
                <Ticket className="h-4 w-4" />
                <span>View Ticket #{conversation.ticket_id}</span>
              </a>
            )}

            {/* Agent Assignment Dropdown - Only visible to admins */}
            {user?.role === 'admin' && (
              <AgentAssignmentDropdown
                conversationId={conversationId}
                channelId={conversation.channel_id}
                currentAssignedTo={conversation.assigned_to}
                onAssignmentChange={() => {
                  loadConversation();
                  onUpdate?.();
                }}
              />
            )}

            {/* Status Dropdown - Enabled for admins or assigned agents */}
            <select
              value={conversation.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={user?.role !== 'admin' && conversation.assigned_to !== user?.id}
              className={`px-3 py-1 rounded text-sm border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-200'
                  : 'bg-white border-gray-300 text-gray-700'
              } ${user?.role !== 'admin' && conversation.assigned_to !== user?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="new">New</option>
              <option value="processing">Processing</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>

            {/* Priority Dropdown - Only visible to admins */}
            {user?.role === 'admin' && (
              <select
                value={conversation.priority}
                onChange={(e) => handlePriorityChange(e.target.value)}
                className={`px-3 py-1 rounded text-sm border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            )}

            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className={`p-2 rounded transition-colors ${
                searchOpen
                  ? 'bg-blue-500 text-white'
                  : theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-600'
              }`}
              title="Search in conversation"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Toggle Sidebar Button */}
            {onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className={`p-2 rounded transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
                title={isSidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
              >
                {isSidebarCollapsed ? (
                  <PanelRightOpen className="h-5 w-5" />
                ) : (
                  <PanelRightClose className="h-5 w-5" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div
            className={`border-b p-3 ${
              theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search messages..."
                  className={`w-full px-3 py-2 pr-10 rounded border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${
                      theme === 'dark'
                        ? 'hover:bg-gray-600 text-gray-400'
                        : 'hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {searchMatches.length > 0 && (
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    {currentMatchIndex + 1} / {searchMatches.length}
                  </span>
                  <button
                    onClick={handlePrevMatch}
                    className={`p-1.5 rounded transition-colors ${
                      theme === 'dark'
                        ? 'hover:bg-gray-700 text-gray-300'
                        : 'hover:bg-gray-200 text-gray-600'
                    }`}
                    title="Previous match"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleNextMatch}
                    className={`p-1.5 rounded transition-colors ${
                      theme === 'dark'
                        ? 'hover:bg-gray-700 text-gray-300'
                        : 'hover:bg-gray-200 text-gray-600'
                    }`}
                    title="Next match"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
              )}

              <button
                onClick={closeSearch}
                className={`p-2 rounded transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-200 text-gray-600'
                }`}
                title="Close search"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {/* Load More Button */}
        {hasMore && messages.length > 0 && (
          <div className="flex justify-center py-4">
            <button
              onClick={loadMoreMessages}
              disabled={loadingMore}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                loadingMore
                  ? theme === 'dark'
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {loadingMore ? 'Loading...' : 'Load Older Messages'}
            </button>
          </div>
        )}

        {/* Debug info */}
        {messages.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              No messages yet...
            </p>
          </div>
        )}

        {/* Typing Indicator */}
        {typingUser && (
          <div className="flex justify-start">
            <div
              className={`max-w-[70%] rounded-lg p-3 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {typingUser.name} is typing
                </span>
                <div className="flex gap-1">
                  <span
                    className="animate-bounce inline-block w-1 h-1 bg-gray-400 rounded-full"
                    style={{ animationDelay: '0ms' }}
                  ></span>
                  <span
                    className="animate-bounce inline-block w-1 h-1 bg-gray-400 rounded-full"
                    style={{ animationDelay: '150ms' }}
                  ></span>
                  <span
                    className="animate-bounce inline-block w-1 h-1 bg-gray-400 rounded-full"
                    style={{ animationDelay: '300ms' }}
                  ></span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages grouped by date */}
        {Object.entries(groupMessagesByDate(messages)).map(([date, dateMessages]) => (
          <div key={date} className="space-y-4">
            {/* Date Separator */}
            <div className="flex justify-center my-4">
              <div
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {formatDateSeparator(date)}
              </div>
            </div>

            {/* Messages for this date */}
            {dateMessages.map((message, localIndex) => {
              const isOutbound = message.direction === 'outbound';
              const isInternalNote = message.is_internal_note;
              // Find the global index of this message
              const globalIndex = messages.findIndex((m) => m.id === message.id);

              return (
                <div
                  key={message.id}
                  data-message-index={globalIndex}
                  className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`overflow-hidden ${
                      isInternalNote
                        ? theme === 'dark'
                          ? 'bg-yellow-900 bg-opacity-30 border border-yellow-700'
                          : 'bg-yellow-50 border border-yellow-200'
                        : ''
                    } rounded-lg p-3 max-w-2/3 flex flex-col gap-1 ${isOutbound && !isInternalNote ? 'items-end' : 'items-start'}`}
                  >
                    {/* Sender info */}
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-xs font-medium ${
                          isInternalNote
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : theme === 'dark'
                              ? 'text-gray-300'
                              : 'text-gray-600'
                        }`}
                      >
                        {message.sender_type === 'agent' &&
                          (message.sender?.id || message.sender_id) && (
                            <code
                              className={`text-xs mx-1 px-1 rounded-md py-0 inline-block ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
                            >
                              ID - {message.sender?.id || message.sender_id}
                            </code>
                          )}
                        {message.sender?.name || message.sender_name || message.sender_type}
                        {isInternalNote && ' (Internal Note)'}
                      </span>
                      {message.sender_type !== 'customer' && (
                        <div
                          className={
                            isInternalNote
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : theme === 'dark'
                                ? 'text-gray-300'
                                : 'text-gray-600'
                          }
                        >
                          {getSenderIcon(message.sender_type)}
                        </div>
                      )}
                    </div>
                    {/* Message content */}
                    {message.content_type === 'form' ? (
                      <FormMessageBubble message={message} theme={theme} isOutbound={isOutbound} />
                    ) : (
                      message.content &&
                      (message.content_type !== 'text' ? (
                        <div
                          className={`prose prose-sm max-w-none ${
                            theme === 'dark' ? 'prose-invert' : ''
                          } ${
                            isInternalNote
                              ? theme === 'dark'
                                ? 'text-yellow-200'
                                : 'text-yellow-900'
                              : isOutbound
                                ? 'text-white prose-headings:text-white prose-p:text-white prose-strong:text-white prose-a:text-blue-200'
                                : theme === 'dark'
                                  ? 'text-gray-100'
                                  : 'text-gray-900'
                          }`}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(message.content)
                          }}
                        />
                      ) : (
                        <p
                          className={`text whitespace-pre-wrap ${
                            isInternalNote
                              ? theme === 'dark'
                                ? 'text-yellow-200'
                                : 'text-yellow-900'
                              : isOutbound
                                ? theme === 'dark'
                                  ? 'text-white bg-blue-600 p-2 rounded-md'
                                  : 'text-white bg-blue-500 p-2 rounded-md'
                                : theme === 'dark'
                                  ? 'text-gray-100 bg-gray-800 p-2 rounded-md'
                                  : 'text-gray-900 bg-white p-2 rounded-md'
                          }`}
                        >
                          {searchQuery
                            ? highlightText(message.content, searchQuery)
                            : message.content}
                        </p>
                      ))
                    )}

                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {message.attachments.map((attachment: any) => {
                          // Check if this is a sticker based on:
                          // 1. File name pattern (image_<numbers> without extension or with .webp)
                          // 2. Small file size (stickers are typically < 100KB)
                          // 3. Square dimensions (stickers are usually square)
                          const fileName = attachment.file_name || '';
                          const isImageNumber = /^image_\d+(\.(webp|png))?$/i.test(fileName);
                          const isSmallFile = attachment.file_size < 100000; // Less than 100KB
                          const isSquare =
                            attachment.width &&
                            attachment.height &&
                            Math.abs(attachment.width - attachment.height) < 50;

                          const isSticker = isImageNumber && (isSmallFile || isSquare);

                          if (attachment.storage_url) {
                            // Display stickers larger without attachment UI (like Meta Business Suite)
                            return (
                              <div key={attachment.id} className="inline-block">
                                <AttachmentPreview
                                  key={attachment.id}
                                  attachment={attachment}
                                  showRemove={false}
                                />
                              </div>
                            );
                          }

                          // Regular attachment display
                          return (
                            <AttachmentPreview
                              key={attachment.id}
                              attachment={attachment}
                              showRemove={false}
                            />
                          );
                        })}
                      </div>
                    )}

                    {/* Timestamp and status */}
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className={`text-xs ${
                          isInternalNote
                            ? 'text-yellow-600 dark:text-yellow-500'
                            : theme === 'dark'
                              ? 'text-gray-400'
                              : 'text-gray-500'
                        }`}
                      >
                        {format(new Date(message.created_at), 'HH:mm')}
                      </span>
                      {isOutbound && !isInternalNote && (
                        <div>{getMessageStatusIcon(message.status)}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Composer */}
      <div className="flex-shrink-0">
        <MessageComposer
          conversationId={conversationId}
          onSendMessage={handleSendMessage}
          isInternalNote={isInternalNote}
          onToggleInternalNote={setIsInternalNote}
          onOpenFormTemplateSelector={() => setShowFormTemplateSelector(true)}
        />
      </div>

      <FormTemplateSelector
        open={showFormTemplateSelector}
        onClose={() => setShowFormTemplateSelector(false)}
        onSelect={handleSendTemplate}
      />

      <TicketEscalationDialog
        conversation={conversation}
        isOpen={showTicketDialog}
        onClose={() => setShowTicketDialog(false)}
        onCreateTicket={handleCreateTicketSubmit}
      />
    </div>
  );
}
